import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import LogoFallback from '/assets/img/bubbanlogo.jpg';

/**
 * HeroSection
 * - Responsive hero that matches background to the logo's main color.
 * - Uses TailwindCSS for layout and small inline styles for color variables.
 * - Props:
 *    - logoSrc: path to the logo image (string). If not provided the fallback import is used.
 *    - accent: hex color for background accent. If not provided, a sensible default is used.
 *
 * Usage:
 *  <HeroSection logoSrc="/assets/bubbanlogo.jpg" accent="#115e32" />
 */

export default function MissionPage({ logoSrc = LogoFallback, accent = '#115e32', pageTitle }) {
  // Inline CSS variables are used so Tailwind classes remain usable while the accent
  // color is dynamic and calculated/selected ahead of time.
	useEffect(() => {
		document.title = "Mission and Vision | Bandarban University"; // Sets the document title
	}, [pageTitle]); 
  
	const rootStyle = {
    '--accent': accent,
    // nicer subtle gradient using the accent color
    backgroundImage: `linear-gradient(135deg, var(--accent) 0%, rgba(17,94,50,0.85) 40%, rgba(255,255,255,0.03) 100%)`,
	};

  return (
	<>
	
    <section
      className="relative w-full overflow-hidden px-6 sm:px-8 lg:px-16"
      style={rootStyle}
      aria-label="Hero"
    >
      {/* Decorative SVG shapes to give depth */}
      <div className="absolute inset-0 pointer-events-none">
        <svg
          className="w-full h-full"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          aria-hidden
        >
          <defs>
            <linearGradient id="g" x1="0" x2="1">
              <stop offset="0" stopOpacity="0.08" stopColor="#ffffff" />
              <stop offset="1" stopOpacity="0.02" stopColor="#000000" />
            </linearGradient>
          </defs>
          <rect width="1440" height="320" fill="url(#g)" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto flex items-center justify-between py-20 lg:py-28">
        {/* Left column: logo (keeps relative positioning so it sits nicely on the hero) */}
        <div className="flex-shrink-0 lg:mr-8 hidden md:flex items-center">
          <img src={logoSrc} alt="Logo" className="w-28 h-28 object-contain drop-shadow-md" />
        </div>

        {/* Center: main content */}
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-sm">
            Mission and Vision of  <span className="inline-block" style={{ color: 'var(--accent)' }}>Bandarban University</span>
          </h1>

          <p className="mt-4 max-w-2xl mx-auto md:mx-0 text-white/90 text-lg sm:text-xl">
           
          </p>

        </div>
		
        
      </div>

      {/* Bottom wave to separate hero from page content */}
      <div className="-mt-6">
        <svg viewBox="0 0 1440 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-20">
          <path d="M0,32 C240,96 480,0 720,32 C960,64 1200,16 1440,32 L1440 80 L0 80 Z" fill="white" opacity="0.92"></path>
        </svg>		
      </div>
	  
    </section>
	<section className="relative bg-gradient-to-br from-gray-50 to-white py-20 px-6 sm:px-10 lg:px-20">
	  <div className="max-w-7xl mx-auto">

		{/* Section Header */}
		<div className="text-center mb-14">
		  <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
			Our <span className="text-[var(--accent)]">Mission & Vision</span>
		  </h1>
		  <div className="w-24 h-1 bg-[var(--accent)] mx-auto mt-4 rounded-full"></div>
		</div>

		<div className="grid md:grid-cols-2 gap-10">

		  {/* Mission Card */}
		  <div className="bg-white shadow-xl rounded-3xl p-8 hover:shadow-2xl transition duration-300 border-t-4 border-[var(--accent)]">
			<h2 className="text-2xl font-bold mb-6 text-[var(--accent)]">
			  🎯 Mission
			</h2>

			<ul className="space-y-4 text-gray-700 leading-relaxed">
			  <li className="flex gap-3">
				<span className="text-[var(--accent)] font-bold">✓</span>
				To create proficient, professional and practically skilled manpower through quality higher education ensuring spiritual and mental development.
			  </li>
			  <li className="flex gap-3">
				<span className="text-[var(--accent)] font-bold">✓</span>
				To develop ethnic communities so they can contribute to the actual development of Bangladesh.
			  </li>
			  <li className="flex gap-3">
				<span className="text-[var(--accent)] font-bold">✓</span>
				To promote knowledge acquisition and practical application.
			  </li>
			  <li className="flex gap-3">
				<span className="text-[var(--accent)] font-bold">✓</span>
				To facilitate research on social, economic, national, international and scientific issues.
			  </li>
			  <li className="flex gap-3">
				<span className="text-[var(--accent)] font-bold">✓</span>
				To build ICT-based education programs preparing students for 21st century challenges.
			  </li>
			</ul>
		  </div>

		  {/* Vision Card */}
		  <div className="bg-white shadow-xl rounded-3xl p-8 hover:shadow-2xl transition duration-300 border-t-4 border-[var(--accent)]">
			<h2 className="text-2xl font-bold mb-6 text-[var(--accent)]">
			  🌟 Vision
			</h2>

			<p className="text-gray-700 leading-relaxed text-justify">
			  The vision of Bandarban University is to impart high-quality education and skills 
			  to students and nurture a creative generation that brings positive change in the 
			  socio-economic, cultural, technical, and political landscape of the country. 
			  Through self-development, students will contribute to nation-building and achieve 
			  excellence in every educational field.
			</p>
		  </div>

		</div>
	  </div>
	</section>
	</>
  );
}

MissionPage.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
};
